import { useState } from 'react'
import { useEventsQuery } from '@/hooks/useDatabase'
import type { DatabaseConnection } from '@/stores/connectionStore'
// Define local interface for Event
interface Event {
  name: string
  eventType?: string
  status?: string
  definer?: string
  timeZone?: string
  onCompletion?: string
  executeAt?: string
  intervalValue?: number
  intervalField?: string
  starts?: string
  ends?: string
  comment?: string
  eventBody?: string
}
import {
  ClockIcon,
  MagnifyingGlassIcon,
  CodeBracketIcon
} from '@heroicons/react/24/outline'

interface EventsViewProps {
  connection: DatabaseConnection
  database: string
  schema?: string
}

export function EventsView({ connection, database, schema }: EventsViewProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')

  const { data: eventsResponse, isLoading, error } = useEventsQuery(
    connection.id,
    database,
    true, // isMySQL
    { enabled: true }
  )

  const allEvents = Array.isArray(eventsResponse) ? eventsResponse : []

  const filteredAndSortedEvents = allEvents
    .filter((event: Event) => {
      if (!searchTerm) return true
      return event.name.toLowerCase().includes(searchTerm.toLowerCase())
    })
    .sort((a: Event, b: Event) => {
      const aValue = a.name || ''
      const bValue = b.name || ''
      const comparison = aValue.localeCompare(bValue)
      return sortOrder === 'asc' ? comparison : -comparison
    })

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
  }

  const getSortIcon = () => {
    return sortOrder === 'asc' ? 
      <div className="w-4 h-4 text-blue-600">↑</div> : 
      <div className="w-4 h-4 text-blue-600">↓</div>
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">
            Error loading events: {error.message}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <ClockIcon className="w-6 h-6 mr-2 text-red-600" />
              Events
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {database}{schema ? `.${schema}` : ''} • {filteredAndSortedEvents.length} events
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search events by name..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value) }}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="min-w-full">
          <div className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600 sticky top-0 z-10">
            <div className="grid grid-cols-10 gap-4 px-4 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              <div 
                className="col-span-8 flex items-center cursor-pointer hover:text-gray-700 dark:hover:text-gray-200"
                onClick={handleSort}
              >
                Event Name
                {getSortIcon()}
              </div>
              <div className="col-span-2">Actions</div>
            </div>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-600">
            {filteredAndSortedEvents.length === 0 ? (
              <div className="text-center py-12">
                <ClockIcon className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">
                  No events found
                </p>
              </div>
            ) : (
              filteredAndSortedEvents.map((event: Event) => (
                <div key={event.name} className="grid grid-cols-10 gap-4 px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="col-span-8 flex items-center">
                    <ClockIcon className="w-4 h-4 text-red-600" />
                    <div className="ml-2">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {event.name}
                      </span>
                    </div>
                  </div>

                  <div className="col-span-2 flex items-center space-x-2">
                    <button type="button"
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                      title="View definition"
                    >
                      <CodeBracketIcon className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 dark:border-gray-700 px-4 py-3 bg-gray-50 dark:bg-gray-700">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div>
            Showing {filteredAndSortedEvents.length} of {allEvents.length} events
          </div>
        </div>
      </div>
    </div>
  )
}
